import {
  susCalculIndicateursCodes,
  susDpiBasculeCodes,
  susFormatRpuCodes,
  susNumeroOrdreCodes,
} from '@/models/sus.type';
import { z } from 'zod';

const optionalText = z.string().trim();

export const susFormSchema = z
  .object({
    finess_geo: z
      .string()
      .trim()
      .regex(/^\d{9}$/),
    numero_ordre: z
      .number()
      .refine(value => (susNumeroOrdreCodes as readonly number[]).includes(value)),
    nom: z.string().trim().min(1).max(50),
    date_gel: z
      .string()
      .nullable()
      .refine(value => !!value),
    adr_num_voie: optionalText.max(5),
    adr_type_voie: optionalText.min(1).max(20),
    adr_nom_voie: optionalText.min(1),
    adr_lieudit: optionalText,
    adr_code_postal: z
      .string()
      .trim()
      .regex(/^(\d{5})?$/),
    adr_commune: optionalText.min(1),
    latitude: z.number().min(-90).max(90).nullable(),
    longitude: z.number().min(-180).max(180).nullable(),
    heure_premier_envoi_quotidien: z.string().regex(/^\d{2}:\d{2}$/),
    reactif_j_1: z.boolean(),
    transmission_autorisee: z.boolean(),
    format_rpu: z.enum(susFormatRpuCodes),
    calcul_automatique_indicateurs: z.enum(susCalculIndicateursCodes).nullable(),
    seuil_bas_quotidien: z.number().int().min(0),
    seuil_haut_quotidien: z.number().int().min(0),
    seuil_bas_duree_passage: z.number().int().min(0),
    seuil_haut_duree_passage: z.number().int().min(0),
    metadonnees: z.object({
      logiciel: optionalText.max(50),
      supra_etab: z.boolean(),
      dpi_version: optionalText,
      dpi_bascule: z.enum(susDpiBasculeCodes).nullable(),
      dpi_bascule_precisions: optionalText,
      transmission_rpu_clos: z.boolean().nullable(),
    }),
    contacts: z
      .array(
        z.object({
          id: z.number().optional(),
          libelle: z.string().trim().min(1),
          email: z.string().trim().email(),
        }),
      )
      .min(1),
  })
  .superRefine((data, ctx) => {
    // La position est choisie sur la carte : latitude et longitude vont ensemble.
    if ((data.latitude == null) !== (data.longitude == null)) {
      ctx.addIssue({ code: 'custom', path: ['latitude'] });
    }
    const pairs = [
      ['seuil_bas_quotidien', 'seuil_haut_quotidien'],
      ['seuil_bas_duree_passage', 'seuil_haut_duree_passage'],
    ] as const;
    for (const [low, high] of pairs) {
      const lowValue = data[low];
      const highValue = data[high];
      if (lowValue != null && highValue != null && lowValue > highValue) {
        ctx.addIssue({ code: 'custom', path: [high] });
      }
    }
  });

export type SusFormValues = z.infer<typeof susFormSchema>;

/** Ligne de contact vierge, pour initialiser le formulaire ou en ajouter une. */
export const emptyContact = { libelle: '', email: '' };
